import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

const AuthContext = createContext({});
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [systemDisabled, setSystemDisabled] = useState(false);

  // Check if system is disabled (login_enabled = false)
  const checkSystemStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/settings/login_enabled`);
      if (response.ok) {
        const data = await response.json();
        const loginEnabled = data.value;
        setSystemDisabled(!loginEnabled);
        return loginEnabled;
      }
    } catch (error) {
      console.error('Error checking system status:', error);
    }
    return true; // Default to enabled if error
  };

  // Helper function to safely execute Supabase queries with retry
  const safeQuery = async (queryFn, maxRetries = 2) => {
    for (let i = 0; i < maxRetries; i++) {
      try {
        const result = await queryFn();
        return result;
      } catch (error) {
        if (error.name === 'AbortError' || error.message?.includes('AbortError')) {
          console.log(`Query aborted, retry ${i + 1}/${maxRetries}`);
          await new Promise(resolve => setTimeout(resolve, 50));
          continue;
        }
        throw error;
      }
    }
    return { data: null, error: { message: 'Max retries exceeded' } };
  };

  // Ask the backend whether the signed-in user (identified by their token,
  // not by an e-mail anyone could type) is an admin.
  const checkAdminStatus = async (accessToken) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/admin-status`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      
      if (!response.ok) {
        console.log('Admin check failed');
        setIsAdmin(false);
        return false;
      }
      
      const data = await response.json();
      
      if (data.is_admin) {
        console.log('User is admin');
        setIsAdmin(true);
        return true;
      }
      
      console.log('User is not admin');
      setIsAdmin(false);
      return false;
    } catch (error) {
      console.error('Admin check exception:', error);
      setIsAdmin(false);
      return false;
    }
  };

  // Fetch user profile
  const fetchProfile = async (userId) => {
    try {
      const { data, error } = await safeQuery(() =>
        supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .single()
      );

      if (data && !error) {
        // Check if user is banned - cannot access at all
        if (data.is_banned) {
          console.log('Banned user attempted to access:', data.email);
          await supabase.auth.signOut();
          setUser(null);
          setSession(null);
          setProfile(null);
          return { error: 'banned', message: 'Hesabınız askıya alınmıştır. Destek ile iletişime geçin.' };
        }
        
        // If user is inactive but NOT banned, they can be reactivated
        // This will be handled in createProfile flow
        
        setProfile(data);
        return data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching profile:', error);
      return null;
    }
  };

  // Create profile if doesn't exist or reactivate if inactive
  const createProfile = async (userId, email) => {
    try {
      // First check if a profile exists (active or inactive)
      const { data: existingProfile } = await safeQuery(() =>
        supabase
          .from('profiles')
          .select('*')
          .eq('email', email)
          .single()
      );

      if (existingProfile) {
        // Check if user is banned - cannot reactivate
        if (existingProfile.is_banned) {
          console.log('Banned user attempted to reactivate:', email);
          await supabase.auth.signOut();
          return { error: 'banned', message: 'Hesabınız askıya alınmıştır. Destek ile iletişime geçin.' };
        }

        // If profile exists and is active, just return it
        if (existingProfile.is_active) {
          setProfile(existingProfile);
          return existingProfile;
        }

        // Profile is inactive - reactivate via backend API (uses RPC to bypass trigger)
        console.log('Reactivating inactive profile for:', email);
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData?.session?.access_token;
        
        if (token) {
          const response = await fetch(`${BACKEND_URL}/api/profile/reactivate`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            const reactivated = await response.json();
            console.log('Successfully reactivated profile for:', email);
            setProfile(reactivated);
            return reactivated;
          } else {
            const errorData = await response.json().catch(() => ({}));
            console.error('Failed to reactivate:', errorData);
            return { error: 'reactivate_failed', message: errorData.detail || 'Hesap aktifleştirilemedi' };
          }
        }
      }

      // Create new profile if no existing one
      const { data, error } = await safeQuery(() =>
        supabase
          .from('profiles')
          .insert({
            id: userId,
            email: email,
            is_active: true
          })
          .select()
          .single()
      );

      if (data && !error) {
        setProfile(data);
        return data;
      }
      return null;
    } catch (error) {
      console.error('Error creating profile:', error);
      return null;
    }
  };

  // Initialize auth state
  useEffect(() => {
    let isMounted = true;
    let timeoutId;

    const initAuth = async () => {
      try {
        // Set a timeout to ensure loading doesn't get stuck
        timeoutId = setTimeout(() => {
          if (isMounted) {
            console.log('Auth initialization timeout - forcing loading to false');
            setLoading(false);
          }
        }, 10000); // 10 second timeout

        // First check if system is enabled
        const loginEnabled = await checkSystemStatus();
        
        const { data, error } = await safeQuery(() => supabase.auth.getSession());
        
        if (!isMounted) return;
        
        if (error) {
          console.error('Get session error:', error);
          setLoading(false);
          return;
        }

        const currentSession = data?.session;
        
        if (currentSession?.user) {
          // Check admin status first
          const userIsAdmin = await checkAdminStatus(currentSession.access_token);
          
          // If system is disabled and user is not admin, sign them out
          if (!loginEnabled && !userIsAdmin) {
            console.log('System disabled, signing out non-admin user');
            await supabase.auth.signOut();
            setUser(null);
            setSession(null);
            setProfile(null);
            setIsAdmin(false);
            setLoading(false);
            return;
          }
          
          setUser(currentSession.user);
          setSession(currentSession);
          
          if (!isMounted) return;
          
          // Only create/fetch profile if NOT admin
          if (!userIsAdmin) {
            let userProfile = await fetchProfile(currentSession.user.id);
            
            // Check if user is banned
            if (userProfile?.error === 'banned') {
              console.log('User access denied: banned');
              setLoading(false);
              return;
            }
            
            // If profile exists but is inactive, try to reactivate
            if (userProfile && !userProfile.is_active && !userProfile.error) {
              console.log('Inactive profile found, attempting reactivation...');
              userProfile = await createProfile(currentSession.user.id, currentSession.user.email);
              
              if (userProfile?.error === 'banned') {
                console.log('Banned user tried to reactivate');
                setLoading(false);
                return;
              }
            }
            
            // If no profile exists, create one
            if (!userProfile && isMounted) {
              userProfile = await createProfile(currentSession.user.id, currentSession.user.email);
              
              // Check if banned user tried to reactivate
              if (userProfile?.error === 'banned') {
                console.log('Banned user tried to reactivate');
                setLoading(false);
                return;
              }
            }
          } else {
            console.log('Admin user detected, skipping profile creation');
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        clearTimeout(timeoutId);
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    initAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      console.log('Auth state changed:', event, newSession?.user?.email);
      
      if (!isMounted) return;
      
      if (newSession?.user) {
        setUser(newSession.user);
        setSession(newSession);
        
        if (!isMounted) return;
        
        // Check admin status FIRST
        const userIsAdmin = await checkAdminStatus(newSession.access_token);
        
        if (!isMounted) return;
        
        // Only create/fetch profile if NOT admin
        if (!userIsAdmin) {
          let userProfile = await fetchProfile(newSession.user.id);
          
          // Check if user is banned
          if (userProfile?.error === 'banned') {
            console.log('User access denied: banned');
            return;
          }
          
          // If profile exists but is inactive, try to reactivate
          if (userProfile && !userProfile.is_active && !userProfile.error) {
            console.log('Inactive profile found, attempting reactivation...');
            userProfile = await createProfile(newSession.user.id, newSession.user.email);
            
            if (userProfile?.error === 'banned') {
              console.log('Banned user tried to reactivate');
              return;
            }
          }
          
          // If no profile exists, create one
          if (!userProfile && isMounted) {
            userProfile = await createProfile(newSession.user.id, newSession.user.email);
            
            // Check if banned user tried to reactivate
            if (userProfile?.error === 'banned') {
              console.log('Banned user tried to reactivate');
              return;
            }
          }
        } else {
          console.log('Admin user detected, skipping profile creation');
          setProfile(null);
        }
      } else {
        setUser(null);
        setSession(null);
        setProfile(null);
        setIsAdmin(false);
      }
      
      if (isMounted) {
        setLoading(false);
      }
    });

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, []);

  // Handle visibility change (when user switches tabs/windows)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        try {
          // Refresh session when tab becomes visible
          const { data, error } = await supabase.auth.getSession();
          
          if (error || !data?.session) {
            // Session expired or invalid - reset state
            setUser(null);
            setSession(null);
            setProfile(null);
            setIsAdmin(false);
            setLoading(false);
            return;
          }
          
          setSession(data.session);
          setUser(data.session.user);
          
          // If system disabled check
          if (user && !isAdmin) {
            const loginEnabled = await checkSystemStatus();
            if (!loginEnabled) {
              await supabase.auth.signOut();
              setUser(null);
              setSession(null);
              setProfile(null);
              setIsAdmin(false);
              window.location.href = '/';
              return;
            }
          }
          
          // Refresh profile if user exists
          if (data.session.user && !isAdmin) {
            await fetchProfile(data.session.user.id);
          }
        } catch (err) {
          console.error('Visibility change error:', err);
        } finally {
          setLoading(false);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, isAdmin]);

  // Sign out
  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      setUser(null);
      setSession(null);
      setProfile(null);
      setIsAdmin(false);
      
      return { success: true };
    } catch (error) {
      console.error('Sign out error:', error);
      return { success: false, error: error.message };
    }
  };

  // Update profile
  const updateProfile = async (updates) => {
    try {
      if (!user) return { success: false, error: 'Not authenticated' };

      const { data, error } = await safeQuery(() =>
        supabase
          .from('profiles')
          .update(updates)
          .eq('id', user.id)
          .select()
          .single()
      );

      if (error) throw error;

      setProfile(data);
      return { success: true, data };
    } catch (error) {
      console.error('Update profile error:', error);
      return { success: false, error: error.message };
    }
  };

  // Delete account (soft delete)
  const deleteAccount = async () => {
    try {
      if (!user) return { success: false, error: 'Not authenticated' };

      const token = session?.access_token;
      if (!token) return { success: false, error: 'No session token' };

      // Call backend to delete account and all related data
      const response = await fetch(`${BACKEND_URL}/api/profile`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Hesap silinemedi');
      }

      // Sign out from Supabase Auth
      await supabase.auth.signOut();
      
      setUser(null);
      setSession(null);
      setProfile(null);
      setIsAdmin(false);
      
      return { success: true };
    } catch (error) {
      console.error('Delete account error:', error);
      return { success: false, error: error.message };
    }
  };

  const value = {
    user,
    session,
    profile,
    isAdmin,
    loading,
    systemDisabled,
    signOut,
    updateProfile,
    deleteAccount,
    checkAdminStatus,
    fetchProfile,
    createProfile,
    checkSystemStatus,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
